import React, { useEffect, useState, useRef, cloneElement } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { ShieldIcon, UsersIcon, BriefcaseIcon, CheckIcon, ClockIcon, ArrowRightIcon, StarIcon, UsersRoundIcon, BookOpenIcon, TrendingUpIcon, AwardIcon, GraduationCapIcon, BarChartIcon, PlayCircleIcon, ChevronRightIcon, BadgeCheckIcon, MousePointerClickIcon } from 'lucide-react';
export const Homepage2 = () => {
  return <div className="flex flex-col w-full min-h-screen bg-white text-charcoal" data-id="element-493">
      <Header data-id="element-494" />
      <main className="flex-grow" data-id="element-495">
        <HeroSection2 data-id="element-496" />
        <CourseCategories data-id="element-497" />
        <FeaturedCourses data-id="element-498" />
        <TrainingStats data-id="element-499" />
        <PopularCourses data-id="element-500" />
        <TestimonialsSection data-id="element-501" />
        <EnterpriseTraining data-id="element-502" />
        <CtaBanner2 data-id="element-503" />
      </main>
      <Footer data-id="element-504" />
    </div>;
};
const HeroSection2 = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="relative pt-28 pb-24 md:pt-36 md:pb-32 lg:pt-44 lg:pb-40 opacity-0 transition-opacity duration-1000 overflow-hidden" data-id="element-505">
      {/* Background with parallax effect */}
      <div className="absolute inset-0 z-0" data-id="element-506">
        <div className="absolute inset-0 bg-cover bg-center transform scale-110 transition-transform duration-3000" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
        transform: isVisible ? 'scale(1)' : 'scale(1.1)'
      }} data-id="element-507"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/90 via-charcoal/80 to-charcoal/95" data-id="element-508"></div>
      </div>
      {/* Animated elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-30" data-id="element-509">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-8 border-teal/20 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateY(0) rotate(0deg)' : 'translateY(-100px) rotate(-45deg)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.3s'
      }} data-id="element-510"></div>
        <div className="absolute bottom-20 right-20 w-60 h-60 rounded-full border-8 border-aqua/10 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateY(0) rotate(0deg)' : 'translateY(100px) rotate(45deg)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.5s'
      }} data-id="element-511"></div>
        <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-full border-4 border-white/30 transform transition-all duration-1000" style={{
        transform: isVisible ? 'translateX(0)' : 'translateX(100px)',
        opacity: isVisible ? 1 : 0,
        transitionDelay: '0.7s'
      }} data-id="element-512"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-513">
        <div className="max-w-4xl mx-auto text-center" data-id="element-514">
          <div className="inline-block px-4 py-1.5 rounded-full bg-teal/20 backdrop-blur-sm text-aqua text-sm font-medium mb-6 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          opacity: isVisible ? 1 : 0
        }} data-id="element-515">
            Trusted by 10,000+ professionals across Europe
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-8 text-white tracking-tight transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.2s'
        }} data-id="element-516">
            Elevate Your Skills. <br className="hidden sm:block" data-id="element-517" />
            <span className="text-gradient bg-gradient-to-r from-teal via-aqua to-teal bg-clip-text text-transparent" data-id="element-518">
              Advance Your Career.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.4s'
        }} data-id="element-519">
            Industry-leading training courses designed by experts and trusted by
            businesses of every size. Get certified and stay ahead.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-5 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.6s'
        }} data-id="element-520">
            <a href="/courses" className="group px-8 py-4 bg-teal text-white font-medium rounded-md transition-all duration-300 shadow-lg transform hover:-translate-y-1 hover:shadow-xl relative overflow-hidden" data-id="element-521">
              <span className="relative z-10" data-id="element-522">Explore Courses</span>
              <div className="absolute inset-0 bg-gradient-to-r from-teal to-aqua opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-523"></div>
            </a>
            <a href="/demo" className="group px-8 py-4 bg-transparent text-white font-medium rounded-md border-2 border-white hover:border-aqua transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center" data-id="element-524">
              <PlayCircleIcon className="h-5 w-5 mr-2 group-hover:text-aqua transition-colors" data-id="element-525" />
              <span className="group-hover:text-aqua transition-colors" data-id="element-526">
                Watch Demo
              </span>
            </a>
          </div>
          <div className="mt-16 flex flex-wrap justify-center gap-8 transform transition-all duration-700" style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          opacity: isVisible ? 1 : 0,
          transitionDelay: '0.8s'
        }} data-id="element-527">
            <div className="flex items-center" data-id="element-528">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-529" />
              <span className="text-white/80" data-id="element-530">Accredited Certificates</span>
            </div>
            <div className="flex items-center" data-id="element-531">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-532" />
              <span className="text-white/80" data-id="element-533">Expert Instructors</span>
            </div>
            <div className="flex items-center" data-id="element-534">
              <BadgeCheckIcon className="h-5 w-5 text-aqua mr-2" data-id="element-535" />
              <span className="text-white/80" data-id="element-536">Flexible Learning</span>
            </div>
          </div>
        </div>
      </div>
      {/* Animated scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center transition-opacity duration-1000" style={{
      opacity: isVisible ? 1 : 0,
      transitionDelay: '1s'
    }} data-id="element-537">
        <span className="text-white/60 text-sm mb-2" data-id="element-538">Scroll to explore</span>
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1" data-id="element-539">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce mt-1" data-id="element-540"></div>
        </div>
      </div>
    </section>;
};
const CourseCategories = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const categories = [{
    icon: <ShieldIcon size={32} className="text-white" data-id="element-541" />,
    title: 'Compliance Training',
    description: 'Essential courses to meet regulatory requirements and industry standards',
    count: '40+ Courses',
    color: 'from-blue-600 to-blue-700',
    link: '/compliance'
  }, {
    icon: <UsersIcon size={32} className="text-white" data-id="element-542" />,
    title: 'Workplace & HR',
    description: 'Build stronger teams with essential workplace and HR training',
    count: '35+ Courses',
    color: 'from-purple-600 to-purple-700',
    link: '/workplace'
  }, {
    icon: <BriefcaseIcon size={32} className="text-white" data-id="element-543" />,
    title: 'Business Skills',
    description: 'Enhance your professional capabilities and business acumen',
    count: '50+ Courses',
    color: 'from-teal to-teal/90',
    link: '/business'
  }];
  return <section ref={sectionRef} className="py-20 bg-white opacity-0 transition-opacity duration-1000" data-id="element-544">
      <div className="container mx-auto px-4 md:px-6" data-id="element-545">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-546">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-547">
            <BookOpenIcon className="h-6 w-6 text-teal" data-id="element-548" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-549">
            Training That Transforms
          </h2>
          <p className="text-xl text-gray-600" data-id="element-550">
            Explore our comprehensive range of training categories designed to
            elevate your skills
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-551">
          {categories.map((category, index) => <div key={index} className="group rounded-2xl overflow-hidden shadow-lg transition-all duration-500 transform hover:-translate-y-2 hover:shadow-xl" data-id="element-552">
              <div className={`bg-gradient-to-br ${category.color} p-8`} data-id="element-553">
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20" data-id="element-554">
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white" data-id="element-555">
                  {category.title}
                </h3>
                <p className="text-white/80 mb-6" data-id="element-556">{category.description}</p>
                <div className="flex items-center justify-between" data-id="element-557">
                  <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm" data-id="element-558">
                    {category.count}
                  </span>
                  <a href={category.link} className="inline-flex items-center text-white font-medium group-hover:translate-x-1 transition-transform" data-id="element-559">
                    Explore Courses
                    <ChevronRightIcon className="h-5 w-5 ml-1" data-id="element-560" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FeaturedCourses = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const featuredCourse = {
    title: 'Food Safety Level 2',
    badge: 'Most Popular',
    description: 'Comprehensive food safety training for all food handlers. Meets EU and UK standards for compliance and best practices.',
    image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    rating: 4.9,
    reviews: 1250,
    students: '12,450+',
    duration: '2 hours',
    modules: 8,
    features: ['Accredited certification', 'Interactive learning', 'Self-paced', '24/7 support']
  };
  return <section ref={sectionRef} className="py-24 bg-gray-50 opacity-0 transition-opacity duration-1000 relative overflow-hidden" data-id="element-561">
      {/* Background patterns */}
      <div className="absolute inset-0 z-0 opacity-30" data-id="element-562">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-teal/10" data-id="element-563"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-aqua/10" data-id="element-564"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-565">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-566">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-567">
            <AwardIcon className="h-6 w-6 text-teal" data-id="element-568" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-569">
            Featured Course
          </h2>
          <p className="text-xl text-gray-600" data-id="element-570">
            Our most popular and highly-rated training experience
          </p>
        </div>
        <div className="max-w-6xl mx-auto" data-id="element-571">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100" data-id="element-572">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-573">
              <div className="relative" data-id="element-574">
                <img src={featuredCourse.image} alt={featuredCourse.title} className="w-full h-full object-cover object-center" style={{
                minHeight: '400px'
              }} data-id="element-575" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" data-id="element-576"></div>
                <div className="absolute top-6 left-6" data-id="element-577">
                  <span className="px-4 py-2 bg-aqua/90 backdrop-blur-sm text-white font-medium rounded-full inline-block" data-id="element-578">
                    {featuredCourse.badge}
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6" data-id="element-579">
                  <div className="flex items-center text-white mb-2" data-id="element-580">
                    <div className="flex" data-id="element-581">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < Math.floor(featuredCourse.rating) ? 'text-amber-400' : 'text-white/30'}`} data-id="element-582" />)}
                    </div>
                    <span className="ml-2 font-medium" data-id="element-583">
                      {featuredCourse.rating}
                    </span>
                    <span className="mx-2" data-id="element-584">•</span>
                    <span data-id="element-585">{featuredCourse.reviews} reviews</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-586">
                    {featuredCourse.title}
                  </h3>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col" data-id="element-587">
                <div className="mb-6" data-id="element-588">
                  <p className="text-xl text-gray-600 mb-6" data-id="element-589">
                    {featuredCourse.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-590">
                    <div className="flex items-center" data-id="element-591">
                      <UsersRoundIcon className="h-5 w-5 text-teal mr-2" data-id="element-592" />
                      <span data-id="element-593">{featuredCourse.students} students</span>
                    </div>
                    <div className="flex items-center" data-id="element-594">
                      <ClockIcon className="h-5 w-5 text-teal mr-2" data-id="element-595" />
                      <span data-id="element-596">{featuredCourse.duration}</span>
                    </div>
                    <div className="flex items-center" data-id="element-597">
                      <BookOpenIcon className="h-5 w-5 text-teal mr-2" data-id="element-598" />
                      <span data-id="element-599">{featuredCourse.modules} modules</span>
                    </div>
                    <div className="flex items-center" data-id="element-600">
                      <AwardIcon className="h-5 w-5 text-teal mr-2" data-id="element-601" />
                      <span data-id="element-602">Certificate included</span>
                    </div>
                  </div>
                  <div className="mb-8" data-id="element-603">
                    <h4 className="text-lg font-bold mb-3 text-charcoal" data-id="element-604">
                      Course Features:
                    </h4>
                    <ul className="space-y-2" data-id="element-605">
                      {featuredCourse.features.map((feature, index) => <li key={index} className="flex items-center" data-id="element-606">
                          <CheckIcon className="h-5 w-5 text-teal mr-2 flex-shrink-0" data-id="element-607" />
                          <span data-id="element-608">{feature}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
                <div className="mt-auto flex flex-col sm:flex-row gap-4" data-id="element-609">
                  <a href="/courses/food-safety-level-2" className="px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 text-center shadow-md hover:shadow-lg transform hover:-translate-y-1" data-id="element-610">
                    Enroll Now
                  </a>
                  <a href="/courses/food-safety-level-2/preview" className="px-8 py-4 bg-transparent text-teal font-medium rounded-md border-2 border-teal hover:bg-teal/5 transition-all duration-300 text-center transform hover:-translate-y-1" data-id="element-611">
                    Preview Course
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrainingStats = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const stats = [{
    value: '125+',
    label: 'Training Courses',
    icon: <BookOpenIcon className="h-8 w-8 text-white" data-id="element-612" />
  }, {
    value: '50,000+',
    label: 'Certified Professionals',
    icon: <AwardIcon className="h-8 w-8 text-white" data-id="element-613" />
  }, {
    value: '98%',
    label: 'Satisfaction Rate',
    icon: <StarIcon className="h-8 w-8 text-white" data-id="element-614" />
  }, {
    value: '24/7',
    label: 'Learning Support',
    icon: <UsersRoundIcon className="h-8 w-8 text-white" data-id="element-615" />
  }];
  return <section ref={sectionRef} className="py-20 bg-gradient-to-br from-charcoal to-charcoal/90 opacity-0 transition-opacity duration-1000 relative overflow-hidden" data-id="element-616">
      {/* Background pattern */}
      <div className="absolute inset-0 z-0 opacity-10" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} data-id="element-617"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-618">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-619">
          {stats.map((stat, index) => <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform transition-all duration-500 hover:-translate-y-2" data-id="element-620">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal mb-4" data-id="element-621">
                {stat.icon}
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2 transition-all duration-1000" style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transitionDelay: `${index * 0.1 + 0.3}s`
          }} data-id="element-622">
                {stat.value}
              </div>
              <p className="text-white/80 text-lg" data-id="element-623">{stat.label}</p>
            </div>)}
        </div>
      </div>
    </section>;
};
const PopularCourses = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const courses = [{
    title: 'Health & Safety Essentials',
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Essential',
    description: 'Core health and safety principles for workplace compliance and risk management.',
    students: '8,320+',
    duration: '3 hours',
    icon: <ShieldIcon size={20} data-id="element-624" />,
    color: 'bg-blue-500'
  }, {
    title: 'Leadership Excellence',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Top Rated',
    description: 'Develop essential leadership skills to inspire and guide your team effectively.',
    students: '9,120+',
    duration: '4 hours',
    icon: <UsersIcon size={20} data-id="element-625" />,
    color: 'bg-indigo-500'
  }, {
    title: 'Digital Marketing Masterclass',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1115&q=80',
    badge: 'Bestseller',
    description: 'Master the fundamentals of digital marketing across multiple platforms.',
    students: '10,780+',
    duration: '5 hours',
    icon: <TrendingUpIcon size={20} data-id="element-626" />,
    color: 'bg-rose-500'
  }, {
    title: 'GDPR Fundamentals',
    image: 'https://images.unsplash.com/photo-1563906267088-b029e7101114?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Updated',
    description: 'Stay compliant with the latest data protection regulations and best practices.',
    students: '7,890+',
    duration: '1.5 hours',
    icon: <CheckIcon size={20} data-id="element-627" />,
    color: 'bg-green-500'
  }, {
    title: 'Cybersecurity Fundamentals',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Critical',
    description: 'Protect your business with essential cybersecurity knowledge and practices.',
    students: '8,450+',
    duration: '2 hours',
    icon: <ShieldIcon size={20} data-id="element-628" />,
    color: 'bg-cyan-500'
  }, {
    title: 'Workplace Diversity & Inclusion',
    image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    badge: 'Essential',
    description: 'Create an inclusive workplace culture that values diversity and promotes equality.',
    students: '6,780+',
    duration: '2 hours',
    icon: <UsersRoundIcon size={20} data-id="element-629" />,
    color: 'bg-purple-500'
  }];
  return <section ref={sectionRef} className="py-24 bg-white opacity-0 transition-opacity duration-1000" data-id="element-630">
      <div className="container mx-auto px-4 md:px-6" data-id="element-631">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-632">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-633">
            <StarIcon className="h-6 w-6 text-teal" data-id="element-634" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-635">
            Most Popular Courses
          </h2>
          <p className="text-xl text-gray-600" data-id="element-636">
            Discover our highest-rated and most-enrolled training courses
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-637">
          {courses.map((course, index) => <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl flex flex-col h-full" data-id="element-638">
              <div className="relative h-48" data-id="element-639">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover" data-id="element-640" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-641"></div>
                <div className="absolute top-4 right-4" data-id="element-642">
                  <span className="px-3 py-1 bg-aqua/80 backdrop-blur-sm text-white text-sm font-medium rounded-full" data-id="element-643">
                    {course.badge}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center text-white" data-id="element-644">
                  <div className={`h-8 w-8 rounded-full ${course.color} flex items-center justify-center mr-2`} data-id="element-645">
                    {cloneElement(course.icon, {
                  className: 'text-white'
                })}
                  </div>
                  <div className="flex items-center text-white/90 text-sm" data-id="element-646">
                    <UsersRoundIcon className="h-3.5 w-3.5 mr-1" data-id="element-647" />
                    <span data-id="element-648">{course.students}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-649">
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-650">
                  {course.title}
                </h3>
                <p className="text-gray-600 mb-4 flex-grow" data-id="element-651">
                  {course.description}
                </p>
                <div className="flex items-center justify-between mt-auto" data-id="element-652">
                  <div className="flex items-center text-gray-500" data-id="element-653">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-654" />
                    <span className="text-sm" data-id="element-655">{course.duration}</span>
                  </div>
                  <a href={`/courses/${course.title.toLowerCase().replace(/\s+/g, '-')}`} className="text-teal font-medium hover:text-aqua transition-colors flex items-center" data-id="element-656">
                    View Course
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-657" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
        <div className="text-center mt-12" data-id="element-658">
          <a href="/courses" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 shadow-lg transform hover:-translate-y-1" data-id="element-659">
            Browse All Courses
            <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-660" />
          </a>
        </div>
      </div>
    </section>;
};
const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const testimonials = [{
    quote: 'The food safety training was incredibly comprehensive and engaging. I feel much more confident in my role now.',
    name: 'Sarah Johnson',
    title: 'Restaurant Manager',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
    rating: 5
  }, {
    quote: 'The leadership course transformed how I manage my team. The practical strategies were easy to implement right away.',
    name: 'Michael Chen',
    title: 'Operations Director',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    rating: 5
  }, {
    quote: 'As a business owner, the GDPR training was invaluable. Clear, concise and exactly what we needed for compliance.',
    name: 'Emma Richards',
    title: 'CEO, TechStart',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=761&q=80',
    rating: 5
  }];
  return <section ref={sectionRef} className="py-24 bg-gray-50 opacity-0 transition-opacity duration-1000" data-id="element-661">
      <div className="container mx-auto px-4 md:px-6" data-id="element-662">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-663">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-664">
            <UsersRoundIcon className="h-6 w-6 text-teal" data-id="element-665" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-666">
            Success Stories
          </h2>
          <p className="text-xl text-gray-600" data-id="element-667">
            Hear from professionals who transformed their careers with our
            training
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-668">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl flex flex-col" data-id="element-669">
              <div className="p-8 flex-grow flex flex-col" data-id="element-670">
                <div className="flex mb-6" data-id="element-671">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < testimonial.rating ? 'text-amber-400' : 'text-gray-200'}`} data-id="element-672" />)}
                </div>
                <blockquote className="text-gray-600 italic mb-6 flex-grow" data-id="element-673">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center mt-auto" data-id="element-674">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-675" />
                  <div data-id="element-676">
                    <p className="font-bold text-charcoal" data-id="element-677">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-500 text-sm" data-id="element-678">{testimonial.title}</p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const EnterpriseTraining = () => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const features = [{
    icon: <UsersRoundIcon className="h-5 w-5 text-teal" data-id="element-679" />,
    title: 'Team Management',
    description: 'Assign and track training for your entire organization'
  }, {
    icon: <BarChartIcon className="h-5 w-5 text-teal" data-id="element-680" />,
    title: 'Advanced Reporting',
    description: 'Detailed analytics and completion tracking'
  }, {
    icon: <AwardIcon className="h-5 w-5 text-teal" data-id="element-681" />,
    title: 'Custom Branding',
    description: 'Your own branded learning portal'
  }, {
    icon: <CheckIcon className="h-5 w-5 text-teal" data-id="element-682" />,
    title: 'Bulk Discounts',
    description: 'Special pricing for volume licenses'
  }];
  return <section ref={sectionRef} className="py-24 bg-white opacity-0 transition-opacity duration-1000" data-id="element-683">
      <div className="container mx-auto px-4 md:px-6" data-id="element-684">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-685">
          <div data-id="element-686">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-4" data-id="element-687">
              <BriefcaseIcon className="h-6 w-6 text-teal" data-id="element-688" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-689">
              Enterprise Training Solutions
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-690">
              Comprehensive training solutions for teams and organizations of
              all sizes. Scale your training efforts with our enterprise
              platform.
            </p>
            <div className="space-y-6 mb-10" data-id="element-691">
              {features.map((feature, index) => <div key={index} className="flex items-start" data-id="element-692">
                  <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-693">
                    {feature.icon}
                  </div>
                  <div data-id="element-694">
                    <h3 className="font-bold text-charcoal text-lg mb-1" data-id="element-695">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-696">{feature.description}</p>
                  </div>
                </div>)}
            </div>
            <a href="/enterprise" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-md hover:bg-gradient-to-r hover:from-teal hover:to-aqua transition-all duration-300 shadow-lg transform hover:-translate-y-1" data-id="element-697">
              Learn More
              <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-698" />
            </a>
          </div>
          <div className="relative" data-id="element-699">
            <div className="rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_50px_rgba(11,117,122,0.2)]" data-id="element-700">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Enterprise Training Dashboard" className="w-full h-full object-cover" data-id="element-701" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" data-id="element-702"></div>
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white p-5 rounded-lg shadow-xl" data-id="element-703">
              <div className="flex items-center" data-id="element-704">
                <div className="h-4 w-4 rounded-full bg-aqua mr-3 animate-pulse" data-id="element-705"></div>
                <span className="text-charcoal font-bold text-lg" data-id="element-706">
                  Team Dashboard
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const CtaBanner2 = () => {
  return <section className="py-20 relative overflow-hidden" data-id="element-707">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal to-teal/80 z-0" style={{
      backgroundImage: `
            linear-gradient(to bottom right, #0b757a, #085c60),
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
          `
    }} data-id="element-708"></div>
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-10" data-id="element-709">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full border-8 border-white/20" data-id="element-710"></div>
        <div className="absolute bottom-20 right-20 w-60 h-60 rounded-full border-8 border-white/10" data-id="element-711"></div>
        <div className="absolute top-1/2 right-1/3 w-20 h-20 rounded-full border-4 border-white/30" data-id="element-712"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-713">
        <div className="max-w-4xl mx-auto text-center" data-id="element-714">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6" data-id="element-715">
            <GraduationCapIcon className="h-8 w-8 text-white" data-id="element-716" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight" data-id="element-717">
            Start Your Learning Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto" data-id="element-718">
            Join thousands of professionals who have transformed their careers
            with our industry-leading training courses.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-719">
            <a href="/courses" className="group px-10 py-4 bg-white text-teal font-medium rounded-md transition-all duration-300 shadow-xl transform hover:-translate-y-1 hover:shadow-2xl relative overflow-hidden" data-id="element-720">
              <span className="relative z-10" data-id="element-721">Browse Courses</span>
              <div className="absolute bottom-0 left-0 w-full h-0 bg-gray-50 transition-all duration-300 group-hover:h-full -z-0" data-id="element-722"></div>
            </a>
            <a href="/contact" className="px-10 py-4 bg-transparent text-white font-medium rounded-md border-2 border-white hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1" data-id="element-723">
              Contact Sales
            </a>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-10" data-id="element-724">
            <div className="flex flex-col items-center" data-id="element-725">
              <div className="text-white/80 mb-2" data-id="element-726">Accredited by</div>
              <div className="text-white font-medium text-lg" data-id="element-727">
                CPD Certified
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-728">
              <div className="text-white/80 mb-2" data-id="element-729">Trusted by</div>
              <div className="text-white font-medium text-lg" data-id="element-730">
                1000+ Businesses
              </div>
            </div>
            <div className="flex flex-col items-center" data-id="element-731">
              <div className="text-white/80 mb-2" data-id="element-732">Rated</div>
              <div className="text-white font-medium text-lg flex items-center" data-id="element-733">
                <span className="mr-1" data-id="element-734">4.9/5</span>
                <div className="flex" data-id="element-735">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-white" data-id="element-736" />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};